/**
 * The split view's claim line: how many homes, where, and what the rail is
 * holding. SITE-44 put the claim over the rail; this is its publisher, so the
 * count rule is pinned by tests instead of living in JSX.
 *
 * THE RULE (Matt 2026-09-23, §0). The figure is the frame's real count. The
 * split view reads at most a display cap of rows (500 pins, and the regional
 * frame serves its first card page before the pins arrive), so a claim built
 * from the rows in hand read "491 homes on this map" over a frame that held
 * more. When the rows in hand fall short of the count:
 *   - the figure is the exact totalCount from the DAL,
 *   - the tail names the order the held rows came in ("newest listed first"), not an
 *     ask range, because a range over the first 500 is not the frame's range,
 *   - a note says how many are on the map, "489 of 3,170 on map" (or listed,
 *     before the map mounts).
 * When every row is in hand the line is what it was: the drawn count and the
 * ask range across it.
 */
import { formatCount } from '@/lib/format/count'
import { resolveSearchSortKey } from '@/lib/search/search-sort-order'
import {
  mapShare,
  mapShareText,
  onMapOfTotalText,
  searchListingCount,
  type ListingCountBasis,
  type MapShare,
} from '@/lib/listing-counts'

/**
 * Sort value (SearchFilters SORT_OPTIONS) -> the order the held rows follow.
 * `newest` orders by the on-market date (lib/search/search-sort-order.ts,
 * Matt 2026-09-23), so the line says "newest listed first", not "newest
 * first", which could read as the latest MLS edit.
 */
export const SORT_ORDER_PHRASES: Readonly<Record<string, string>> = {
  newest: 'newest listed first',
  oldest: 'oldest listed first',
  price_asc: 'lowest price first',
  price_desc: 'highest price first',
  price_per_sqft_asc: 'lowest price per sq ft first',
  price_per_sqft_desc: 'highest price per sq ft first',
  year_newest: 'newest built first',
  year_oldest: 'oldest built first',
}

/**
 * The Sold scope's date sorts order by the close date (search-sort-order.ts
 * searchSortLabel: "Recently sold" / "Oldest sold"), so the line says so.
 */
export const SOLD_SORT_ORDER_PHRASES: Readonly<Record<'newest' | 'oldest', string>> = {
  newest: 'most recently sold first',
  oldest: 'oldest sold first',
}

export function sortOrderPhrase(sort: string | null | undefined, scope: { sold?: boolean } = {}): string {
  // Resolved the way the sort control names it (legacy spellings, unknown ->
  // newest), so the claim and the control never name two orders.
  const key = resolveSearchSortKey(sort)
  if (scope.sold && (key === 'newest' || key === 'oldest')) return SOLD_SORT_ORDER_PHRASES[key]
  return SORT_ORDER_PHRASES[key] ?? SORT_ORDER_PHRASES.newest
}

export type SplitClaim = {
  /** The number the line leads with. */
  figure: number
  /**
   * True when `figure` is a floor, not a count (the uncapped count did not
   * come back in time, or a drawn shape filtered a capped read): the line
   * prints it as "501+".
   */
  figureIsFloor: boolean
  /** 'home' | 'homes', agreeing with `figure`. */
  noun: 'home' | 'homes'
  /** ' in Central Oregon' for the regional frame, else ' on this map'. */
  where: string
  /** True when the rows in hand are fewer than the frame's count. */
  truncated: boolean
  /** Ask range across the held rows; only when nothing is held back. */
  range: { low: number; high: number } | null
  /** The held rows' order, only when truncated: 'newest listed first'. */
  order: string | null
  /**
   * '489 of 3,170 on map' when the map draws fewer pins than the figure;
   * 'first 48 listed' when truncated before the map mounts; else null.
   */
  note: string | null
  /**
   * W23 (site audit 2026-10-21, 2.2): the frame holds fewer homes than the
   * list counts for the same filters ("134 of 269 on the map"). Null when
   * there is no gap, or no list figure to compare (lib/listing-counts mapShare).
   */
  share: MapShare | null
  /** The share in words, printed beside the note: "134 of 269 on the map". */
  shareNote: string | null
  /** The section 0 trace behind the "Source" disclosure. */
  source: string
  /**
   * W1 (Muse audit 2026-10-20): what the figure counts, in plain words, from
   * searchListingCount ("all home types", or "the home types you chose").
   * Null on the Sold scope, which counts closings, not listings for sale.
   */
  basis: ListingCountBasis | null
}

export function publishSplitClaim(input: {
  /** Rows the list and pins draw (after the viewer's hidden homes). */
  visibleCount: number
  /** Rows the fetch returned (before hidden homes). */
  rowsInHand: number
  /** The DAL's exact count for the frame and filters. */
  totalCount: number
  /**
   * True when totalCount is only a floor (the rows fetched): the exact count
   * did not come back in time, or a drawn shape filtered a read that capped.
   * The line then says "N+" and the trace says "at least"; a floor is never
   * published as a count (section 0).
   */
  countIsFloor?: boolean
  sort: string | null | undefined
  /** True on the Sold scope, where the date sorts follow the close date. */
  sold?: boolean
  /** True when the reader chose a property type (the basis is then theirs, not every type). */
  typeFiltered?: boolean
  /** 'Central Oregon' for the regional frame; null for a map frame. */
  frameLabel: string | null
  /** False while the map has not mounted (phones open on the list). */
  mapMounted: boolean
  /**
   * Pins the map draws: the visible rows with a coordinate (default
   * visibleCount). Fewer than the figure prints "{onMap} of {figure} on map".
   */
  onMapCount?: number
  low: number | null
  high: number | null
  /** Held rows that publish a whole-property ask (the range population). */
  askCount: number
  /** Rows behind the $/sq ft band, when one is drawn. */
  bandCount: number | null
  /**
   * W23: the list's total for the same filters with no map frame (the place
   * by name, countSearchListings), when the caller can vouch it counts the
   * same status and filters as the frame. Never on the regional frame, which
   * is the list's own population.
   */
  listTotal?: number | null
}): SplitClaim | null {
  const total = Math.max(0, Math.floor(input.totalCount))
  const visible = Math.max(0, Math.floor(input.visibleCount))
  const floor = input.countIsFloor === true
  // A floor says more homes match than were read, so the rows in hand are a
  // part of the set even when they number the floor itself (a drawn shape
  // filtered out of a capped read): no range, the order, "N+".
  const truncated = input.rowsInHand < total || floor
  const counted = searchListingCount(truncated ? total : visible, { typeFiltered: input.typeFiltered })
  const figure = counted.count
  if (figure <= 0) return null

  const where = input.frameLabel ? ` in ${input.frameLabel}` : ' on this map'
  const range =
    !truncated && input.low != null && input.high != null ? { low: input.low, high: input.high } : null
  const order = truncated ? sortOrderPhrase(input.sort, { sold: input.sold }) : null
  // Audit v2 2.2: the map's share of the figure reads "489 of 3,170 on map",
  // from the count service, whenever the map draws fewer pins than the figure
  // (the display cap, or a held row with no coordinate). Before the map
  // mounts the held rows are only listed.
  const onMap = Math.max(0, Math.floor(input.onMapCount ?? visible))
  const note = input.mapMounted
    ? onMapOfTotalText(onMap, counted, { totalIsFloor: floor })
    : truncated
      ? `first ${formatCount(visible)} listed`
      : null

  // The frame's own count against the list's: a home outside the frame, or
  // with no recorded map location, is in the list and not on the map. A floor
  // is not a count, so it never makes a share.
  const share =
    !input.frameLabel && !input.sold && !floor ? mapShare(total, input.listTotal) : null
  const shareNote = share
    ? mapShareText(share, truncated ? 'inside this map frame' : input.mapMounted ? 'on the map' : 'in this map area')
    : null

  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)
  const scope = input.frameLabel
    ? `for ${input.frameLabel}, the service area this site covers`
    : 'for this map frame'
  const parts: string[] = [
    floor
      ? `Oregon Data Share, read live ${scope}: at least ${formatCount(total)} listings match; the full count was not read.`
      : `Oregon Data Share, read live ${scope}: ${formatCount(total)} ${plural(total, 'listing matches', 'listings match')}.`,
  ]
  if (truncated) {
    parts.push(
      `The first ${formatCount(visible)}, ${order}, are ${input.mapMounted ? 'listed and pinned' : 'listed'}; that is the display cap, not the whole set.`,
    )
  } else if (range) {
    parts.push(
      `The range is the lowest and highest ask among the ${formatCount(input.askCount)} of them that publish a whole-property price; a fractional share and a commercial lease rate are withheld.`,
    )
  }
  if (share) {
    parts.push(
      `The list counts ${formatCount(share.of)} ${plural(share.of, 'home', 'homes')} for these filters with no map frame; ${formatCount(share.onMap)} of them sit inside this one. A home outside the frame, or with no recorded map location, is in the list but not on the map.`,
    )
  }
  if (input.bandCount != null && input.bandCount > 0) {
    parts.push(
      `The band on each card is the middle half of the ${formatCount(input.bandCount)} ${truncated ? 'shown' : 'here'} that publish a price per square foot.`,
    )
  }

  return {
    figure,
    figureIsFloor: floor,
    noun: figure === 1 && !floor ? 'home' : 'homes',
    where,
    truncated,
    range,
    order,
    note,
    share,
    shareNote,
    source: parts.join(' '),
    basis: input.sold ? null : counted.basis,
  }
}
